import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import updateSearchFields from "@insite/client-framework/Store/Pages/RfqMyQuotes/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";
import React, { ChangeEvent } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quotesDataView: getQuotesDataView(state, state.pages.rfqMyQuotes.getQuotesParameter),
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqMyQuotesPaginationStyles {
    pagination?: PaginationPresentationProps;
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
    resultCount?: InjectableCss;
}

export const rfqMyQuotesPaginationStyles: RfqMyQuotesPaginationStyles = {
    wrapper: {
        css: css`
            margin-top: .625rem;
            color: ${themeColors.colorCharcoalGrey};
            font-size: ${themeFontSizes.size14};
            letter-spacing: 0;
            display: flex;
            padding: 10px 0;
            margin-bottom: 0;
            margin-top: 5px;
            border-top: 1px solid ${themeColors.colorBorderNew};
            border-bottom: 1px solid ${themeColors.colorBorderNew};
            align-items: center;
            z-index: 1;
            position: relative;

            .quote-paging {
                margin: 0;
                width: auto;
                justify-content: flex-start;
                padding-left: 1.5625rem;
                position: absolute;
                right: 0;

                > span {
                    label {
                        color: ${themeColors.colorMidGrey};
                        font-family: 'Roboto', sans-serif;
                        font-size: 0.875rem;
                        font-weight: 500;
                        letter-spacing: 0;
                        padding: 0.625rem 0.9375rem 0 0;
                    }
                }

                select[data-test-selector="paginationPerPageSelect"] {
                    min-width: 79px;
                    border-color: ${themeColors.colorColumbiaBlue} !important;
                    opacity: .93;
                    background-color: #fff !important;
                    color: ${themeColors.colorSquidInk} !important;
                    letter-spacing: 0.16px;
                }
            }

            nav {
                display: flex;
                justify-content: center;

                @media screen and (min-width: 1280px) {
                    width: 70%;
                    margin-top: 0;
                    ul.pagination-arrow li:nth-child(3) {
                        margin-left: 3.4375rem;
                    }

                    ul.pagination-arrow li:nth-last-child(2) {
                        margin-left: 3.4375rem;
                    }
                }

                @media screen and (min-width: 1199px) {
                    margin-top: 0;
                    margin-left: 3.75rem;
                }

                @media screen and (min-width: 1400px) {
                    margin-left: 6.875rem;
                }
            }

            @media screen and (max-width: 1279px) {
                nav {
                    flex-basis: 100%;
                    max-width: 100%;
                }
                .quote-paging {
                    width: 100%;
                    flex-basis: 100%;
                    padding-left: 0;
                    flex-wrap: wrap;
                    justify-content: flex-end;
                    position: unset;
                    > span {
                        > div {
                            flex-direction: column;
                            align-items: flex-end;
                            padding-right: 0;
                        }
                        label {
                            text-align: left;
                            line-height: 31px;
                            padding: 0;

                            + div {
                                min-width: 155px;
                            }
                        }
                    }
                }
                
                .quote-result-count {
                    font-weight: ${themeFontWeights.fontWeightSemiBold};
                    position: absolute;
                    left: 0;
                    width: auto;
                    top: 49px;
                }
            }

        `,
    },
    countText: {
        css: css`
            white-space: nowrap;
        `,
    },
    queryText: {
        css: css`
            white-space: nowrap;
            font-weight: bold;
        `,
    },
    resultCount: {
        css: css`
            display: flex;
            width: auto;
            color: ${themeColors.colorMidGrey};
            font-family: 'Lato',sans-serif;
            font-size: 14px;
            font-style: italic;
            letter-spacing: 0;
            line-height: 31px;

            span {
                color: ${themeColors.colorMidGrey};
                display: inline-block;
                padding-right: 5px;
            }

            @media screen and (min-width: 1280px) {
                width: auto;
            }
        `,
    },
};

const styles = rfqMyQuotesPaginationStyles;

const RfqQuotePagination = ({ quotesDataView, updateSearchFields }: Props) => {
    const changePage = (newPageIndex: number) => {
        updateSearchFields({
            page: newPageIndex,
        });
    };

    const changeResultsPerPage = (event: ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        updateSearchFields({
            page: 1,
            pageSize: newPageSize,
        });
    };

    if (!quotesDataView.value) {
        return null;
    }
    
    const { pagination } = quotesDataView;
    
    if (!pagination || pagination.totalItemCount === 0) {
        return null;
    }

    const itemsText = (pagination.totalItemCount !== 1 ? " Results" : " Result");
    const pageSizeNew = (pagination.totalItemCount < pagination.pageSize ? pagination.totalItemCount : pagination.pageSize ); 
    return (
        <Typography as="div" {...styles.wrapper}>
            <StyledWrapper {...styles.resultCount} className="quote-result-count"  data-test-selector="quote_resultCount">
                <Typography {...styles.itemsText}>Showing </Typography>
                <Typography {...styles.countText} data-test-selector="quote_CountText">
                    {pageSizeNew}
                </Typography>
                <Typography {...styles.itemsText}> of </Typography>
                <Typography {...styles.countText} data-test-selector="quote_CountText">
                    {pagination.totalItemCount}
                </Typography>
                <Typography {...styles.itemsText}>{translate(itemsText)}</Typography>
            </StyledWrapper>
            
            <Pagination
            {...styles.pagination}
            resultsCount={pagination.totalItemCount}
            currentPage={pagination.page}
            resultsPerPage={pagination.pageSize}
            resultsPerPageOptions={pagination.pageSizeOptions}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            data-test-selector="rfqMyQuotes_Pagination"
            prevPage={pagination.prevPageUri}
            nextPage={pagination.nextPageUri}
            className="quote-paging"
        />
        </Typography>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqQuotePagination),
    definition: {
        group: "RFQ My Quotes",
        displayName: "Pagination",
        // allowedContexts: ["RfqMyQuotesPage"],
    },
};

export default widgetModule;
